export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type HttpRequestConfig = {
  url: string;
  method: HttpMethod;
  headers: Record<string, string>;
  body?: unknown;
  signal?: AbortSignal;
};

export class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly url: string,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export type RequestInterceptor = (
  config: HttpRequestConfig,
) => HttpRequestConfig | Promise<HttpRequestConfig>;

/**
 * Transforma a Response de sucesso antes do parse final do corpo. Diferente de `ErrorInterceptor`,
 * uma exceção lançada aqui propaga diretamente e NÃO passa pelos `errorInterceptors` — estes só
 * tratam falha de fetch ou status não-ok, nunca um erro lançado por um `ResponseInterceptor`.
 */
export type ResponseInterceptor = (
  response: Response,
  config: HttpRequestConfig,
) => Response | Promise<Response>;

/** Recebe o erro e pode relançar (padrão) ou retornar uma Response de recuperação. */
export type ErrorInterceptor = (
  error: unknown,
  config: HttpRequestConfig,
) => Response | Promise<Response>;

export type HttpClientConfig = {
  baseURL?: string;
  headers?: Record<string, string>;
  interceptors?: {
    request?: RequestInterceptor[];
    response?: ResponseInterceptor[];
    error?: ErrorInterceptor[];
  };
};

type HttpRequestOptions = Partial<Omit<HttpRequestConfig, 'url' | 'method'>>;

export type HttpClient = {
  get<T>(url: string, options?: HttpRequestOptions): Promise<T>;
  post<T>(url: string, body?: unknown, options?: Omit<HttpRequestOptions, 'body'>): Promise<T>;
  patch<T>(url: string, body?: unknown, options?: Omit<HttpRequestOptions, 'body'>): Promise<T>;
};

const ABSOLUTE_URL_PATTERN = /^https?:\/\//;

function resolveUrl(baseURL: string | undefined, url: string): string {
  if (ABSOLUTE_URL_PATTERN.test(url) || !baseURL) {
    return url;
  }
  return `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
}

async function runErrorInterceptors(
  error: unknown,
  config: HttpRequestConfig,
  interceptors: ErrorInterceptor[],
): Promise<Response> {
  let lastError = error;

  for (const interceptor of interceptors) {
    try {
      return await interceptor(lastError, config);
    } catch (nextError) {
      lastError = nextError;
    }
  }

  throw lastError;
}

export function createHttpClient(clientConfig: HttpClientConfig = {}): HttpClient {
  const requestInterceptors = clientConfig.interceptors?.request ?? [];
  const responseInterceptors = clientConfig.interceptors?.response ?? [];
  const errorInterceptors = clientConfig.interceptors?.error ?? [];

  async function request<T>(
    url: string,
    method: HttpMethod,
    options: HttpRequestOptions = {},
  ): Promise<T> {
    const isFormData = options.body instanceof FormData;
    const headers = { ...clientConfig.headers, ...options.headers };
    if (options.body !== undefined && !isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    let requestConfig: HttpRequestConfig = {
      url: resolveUrl(clientConfig.baseURL, url),
      method,
      headers,
      body: options.body,
      signal: options.signal,
    };

    for (const interceptor of requestInterceptors) {
      requestConfig = await interceptor(requestConfig);
    }

    let response: Response;
    try {
      response = await fetch(requestConfig.url, {
        method: requestConfig.method,
        headers: requestConfig.headers,
        body:
          requestConfig.body === undefined
            ? undefined
            : requestConfig.body instanceof FormData
              ? requestConfig.body
              : JSON.stringify(requestConfig.body),
        signal: requestConfig.signal,
      });

      if (!response.ok) {
        throw new HttpError(
          `Falha ao buscar ${requestConfig.url}: HTTP ${response.status}`,
          response.status,
          requestConfig.url,
        );
      }
    } catch (error) {
      response = await runErrorInterceptors(error, requestConfig, errorInterceptors);
    }

    for (const interceptor of responseInterceptors) {
      response = await interceptor(response, requestConfig);
    }

    // Corpo vazio é um caso esperado (ex.: PATCH que retorna 200/204 sem body) e é detectado
    // explicitamente aqui, sem depender de uma falha de parse do JSON para inferir isso.
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }

    try {
      return (await response.json()) as T;
    } catch (error) {
      // Corpo não vazio que não é JSON válido é um bug real do backend — não interrompe o
      // fluxo (mantém o contrato atual de retornar undefined), mas é logado em dev.
      if (process.env.NODE_ENV === 'development') {
        console.error('[HTTP] Corpo de resposta não é JSON válido: ', error);
      }
      return undefined as T;
    }
  }

  return {
    get: (url, options) => request(url, 'GET', options),
    post: (url, body, options) => request(url, 'POST', { ...options, body }),
    patch: (url, body, options) => request(url, 'PATCH', { ...options, body }),
  };
}
